/**
 * Order Lifecycle — statü sabitleri, rol-izin matrisi, geçiş kuralları.
 *
 * 7 aşamalı model:
 *   Neu → Kommissioniert → Versandbereit → Unterwegs
 *       → Geliefert → Berechnet → Abgeschlossen
 *   (+ Storniert her aşamada admin tarafından)
 *
 * Bu modül UI'lara paylaşılan "hangi butonun görüneceği / hangi geçişe
 * izin verileceği" kararını dağıtır. Sunucu tarafında CHECK constraint
 * değerleri zorlar; burada ek olarak rol bazlı UI gate'i yapıyoruz.
 */

export const ORDER_STATUSES = [
  'Neu',
  'Kommissioniert',
  'Versandbereit',
  'Unterwegs',
  'Geliefert',
  'Berechnet',
  'Abgeschlossen',
  'Storniert',
];

export const ORDER_STATUS = Object.freeze({
  NEU: 'Neu',
  KOMMISSIONIERT: 'Kommissioniert',
  VERSANDBEREIT: 'Versandbereit',
  UNTERWEGS: 'Unterwegs',
  GELIEFERT: 'Geliefert',
  BERECHNET: 'Berechnet',
  ABGESCHLOSSEN: 'Abgeschlossen',
  STORNIERT: 'Storniert',
});

/**
 * Rol → izinli geçiş haritası.
 * Key: mevcut statü. Value: { role: [izinli yeni statüler] }
 *
 * Admin her aşamada Storniert yapabilir (global override, aşağıda).
 */
const TRANSITIONS = {
  Neu: {
    warehouse: ['Kommissioniert'],
    admin: ['Kommissioniert', 'Storniert'],
  },
  Kommissioniert: {
    warehouse: ['Versandbereit'],
    admin: ['Versandbereit', 'Storniert'],
  },
  // Versand alanı Warenausgang'a konsolide edildi: depo rolü de sevkiyat
  // geçişlerini yapabilir. Shipping rolü ayrı ekran kullanmaya devam edebilir.
  Versandbereit: {
    warehouse: ['Unterwegs'],
    shipping: ['Unterwegs'],
    admin: ['Unterwegs', 'Storniert'],
  },
  Unterwegs: {
    warehouse: ['Geliefert'],
    shipping: ['Geliefert'],
    admin: ['Geliefert', 'Storniert'],
  },
  Geliefert: {
    admin: ['Berechnet', 'Storniert'],
  },
  Berechnet: {
    admin: ['Abgeschlossen', 'Storniert'],
  },
  Abgeschlossen: {
    // Terminal — admin geri alamaz (Storniert için credit note süreci ayrı).
  },
  Storniert: {
    // Terminal.
  },
};

/**
 * Kullanıcı rolü verilen mevcut statüden hedef statüye geçebilir mi?
 *
 * Admin'e global Storniert izni verilir (tüm aşamalardan).
 *
 * @param {string} currentStatus
 * @param {string} nextStatus
 * @param {string} role  — 'admin' | 'warehouse' | 'shipping' | 'agent'
 * @returns {boolean}
 */
export function canTransitionTo(currentStatus, nextStatus, role) {
  if (!currentStatus || !nextStatus || !role) return false;
  if (role === 'admin' && nextStatus === 'Storniert'
      && currentStatus !== 'Storniert' && currentStatus !== 'Abgeschlossen') {
    return true;
  }
  const row = TRANSITIONS[currentStatus];
  if (!row) return false;
  const allowed = row[role] || [];
  return allowed.includes(nextStatus);
}

/**
 * Bir rol, bu statüdeki siparişte hangi aksiyon butonlarını görebilir?
 *
 * @returns {Array<{ label: string, nextStatus: string, intent: string }>}
 *   intent: UI renk tonu için ipucu (primary | danger | neutral).
 */
export function getAvailableActions(currentStatus, role) {
  const actions = [];
  const row = TRANSITIONS[currentStatus] || {};
  const allowedByRole = row[role] || [];

  for (const next of allowedByRole) {
    actions.push({
      label: ACTION_LABELS[`${currentStatus}→${next}`] || next,
      nextStatus: next,
      intent: next === 'Storniert' ? 'danger' : 'primary',
    });
  }

  // Admin global Storniert override
  if (role === 'admin'
      && !allowedByRole.includes('Storniert')
      && currentStatus !== 'Storniert'
      && currentStatus !== 'Abgeschlossen') {
    actions.push({
      label: 'Stornieren',
      nextStatus: 'Storniert',
      intent: 'danger',
    });
  }

  return actions;
}

/**
 * Buton etiketleri — (currentStatus→nextStatus) anahtarı.
 * Listede yoksa fallback olarak hedef statü adı kullanılır.
 */
const ACTION_LABELS = {
  'Neu→Kommissioniert':           'Kommissionieren',
  'Kommissioniert→Versandbereit': 'Versandbereit markieren',
  'Versandbereit→Unterwegs':      'Abfahrt',
  'Unterwegs→Geliefert':          'Lieferung bestätigen',
  'Geliefert→Berechnet':          'Rechnung erstellen',
  'Berechnet→Abgeschlossen':      'Abschließen',
};

/**
 * Terminal statü mü? (artık aksiyon alınamaz)
 */
export function isTerminalStatus(status) {
  return status === 'Abgeschlossen' || status === 'Storniert';
}
